import { ADD_RAILS_TO_BOM, RAILS_LOADED, FETCH_RAILS_ACTION, SELECT_ONLY_SMHD_RAILS, UNSELECT_ONLY_SMHD_RAILS_OPTION, RAIL_TYPE_LOADED } from 'actions';
import { hitApi, ObservableAjax } from '__common/utils/api';
import { RailsSelectorActionTypes } from 'actionsConstants';
import { saveBom } from '../../utils/saveBom';
import { getRailsUrl } from './utils/railsApi';
import { safeOfType as ofType } from '__common/utils/safeOfType';
import { Action } from '__common/store/action';
import { ADD_CLAMPS, ADD_NXT_CLAMPS } from 'bom/bomActions';
import { isULA } from '__common/constants/products';
import { Observable, EMPTY, from, concat, of } from 'rxjs';
import { debounceTime, switchMap, filter, withLatestFrom } from 'rxjs/operators';
import { showErrorAlert } from '__common/modules/alerts';
import nprogress from 'nprogress';
import { dispatch } from '__common/store';
import { FETCHING_RAILS } from './railsSelectorActions';
import { EMPTY_APP_ACTION } from 'app/appActions';

function addRailsClampsToBom(data: any) {
  if (data?.clamps) {
    return ADD_NXT_CLAMPS(data);
  }

  if (data?.midclamps || data?.endclamps) {
    return ADD_CLAMPS({
      midclamps: data.midclamps || [],
      endclamps: data.endclamps || [],
    });
  }

  return EMPTY_APP_ACTION();
}

function saveBomAfterRailsUpdate() {
  try {
    saveBom(true);
  } catch (error) {
    console.error('Rails updated, but BOM autosave failed:', error);
  }
}

function saveBomAfterRailsUpdateAction() {
  saveBomAfterRailsUpdate();
  return EMPTY_APP_ACTION();
}

function showRailsEmptyAlert() {
  showErrorAlert('Cannot fetch rails');
  return EMPTY_APP_ACTION();
}

function notifyIfRailsEmpty(data: any) {
  const rails = data && Array.isArray(data.rails) ? data.rails : [];
  const mid = Array.isArray(data?.midclamps) ? data.midclamps.length : 0;
  const end = Array.isArray(data?.endclamps) ? data.endclamps.length : 0;
  return rails.length === 0 && mid === 0 && end === 0
    ? showRailsEmptyAlert()
    : EMPTY_APP_ACTION();
}

export function fetchRails(action$: Observable<any>, state$: Observable<appState>) {
  return action$.pipe(
    ofType(RailsSelectorActionTypes.FETCH_RAILS),
    debounceTime(2000),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const fetchRailsAction = action as Action<FETCH_RAILS_ACTION>;
      const { projectConfiguration: { productId } } = state;
      const railsList = fetchRailsAction.payload.rails || state.railsSelector.railsList;
      const selectOnlySMHDRails = fetchRailsAction.payload.selectOnlySMHDRails ?? state.railsSelector.selectOnlySMHDRails;
      // Mark rails as fetching before the request, matching master/develop.
      // FETCHING_RAILS used to sit in onSuccess next to RAILS_LOADED, so
      // railsFetching flipped true and false in the same tick and the part
      // list updated in place with no loading state.
      const onSuccessActions: Function[] = isULA(productId)
        ? [FETCHING_RAILS, ADD_RAILS_TO_BOM, RAILS_LOADED, saveBomAfterRailsUpdateAction, RAIL_TYPE_LOADED, notifyIfRailsEmpty]
        : [FETCHING_RAILS, ADD_RAILS_TO_BOM, RAILS_LOADED, saveBomAfterRailsUpdateAction, addRailsClampsToBom, notifyIfRailsEmpty];

      if (!fetchRailsAction.payload.skipBomSave) {
        // Save again after clamps land. The earlier save persists the rail
        // selection even if the clamp merge leaves a part the qty check rejects.
        onSuccessActions.push(saveBomAfterRailsUpdateAction);
      } else {
        const saveIndex = onSuccessActions.indexOf(saveBomAfterRailsUpdateAction);
        if (saveIndex !== -1) onSuccessActions.splice(saveIndex, 1);
      }

      return concat(
        of(FETCHING_RAILS()),
        ObservableAjax({
          until$: action$.pipe(ofType(RailsSelectorActionTypes.FETCH_RAILS)),
          onSuccess: onSuccessActions,
          // Do not set onErrorMessage. A cancelled or follow-up rails call
          // (switchMap abort, status 0) is reported as an ajax error even
          // when a later call refreshes the part list, and that surfaces a
          // false "Cannot fetch rails" toast. Real empty results are still
          // reported by notifyIfRailsEmpty.
          onErrorAction: () => {
            nprogress.done();
            // onErrorAction's return value is ignored. Dispatch RAILS_LOADED
            // so railsFetching is cleared and the part list is not stuck
            // on the loader after a failed rails request.
            dispatch(RAILS_LOADED());
          },
          link: hitApi('get', getRailsUrl(fetchRailsAction.payload.projectId, railsList, fetchRailsAction.payload.useStandardClamps, selectOnlySMHDRails)),
        }),
      );
    })
  );
}

export function selectRailTypeFromBom(action$: Observable<any>, state$: Observable<appState>): Observable <any>{
  return action$.pipe(
    ofType(RailsSelectorActionTypes.SELECT_RAILS_FROM_API),
    withLatestFrom(state$),
    filter(([action, state]) => {
      const { projectConfiguration: { productId } } = state;
      return isULA(productId);
    }),
    switchMap(([action, state])=> {
      const { bom: { extraParameters, isClampsChanged }, projectConfiguration: { projectEnvConfig: { clamp_type } } } = state;
      const actions = [];
      
      if(extraParameters.select_only_smhd_rails) {
        actions.push(SELECT_ONLY_SMHD_RAILS());
      } else {
        actions.push(UNSELECT_ONLY_SMHD_RAILS_OPTION());
      }
      
      if(isClampsChanged){ // If user selected clamp fails, warning message will be displayed in the bom page
        let message = '';
        if(clamp_type === '02') {
          message = "Note: Clamps option has been switched to top clamp as bottom clamp couldn't counter the higher load conditions.";
        } else {
          message = "Note: Clamps option has been switched to bottom clamp as top clamp couldn't counter the higher load conditions.";
        }
        showErrorAlert(message);
      }
      
      return actions.length > 0 ? from(actions) : EMPTY;
    })
  );
}
